import React from "react";
import { PhotoUploader } from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Navigation, MapPin, Pencil } from "lucide-react";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";

const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

/**
 * One pool inside a DriverTicketCard: address/drive link, access info (view +
 * edit), gallons input and the before/after photo uploaders.
 */
export default function DriverPoolSection({ t, d, idx, pools, setPools, entryEdit }) {
  const { entryEditIdx, entryDraft, setEntryDraft, startEntryEdit, saveEntryEdit, cancelEntryEdit } = entryEdit;
  return (
    <div className="border border-border rounded-sm p-3 space-y-2" data-testid={`driver-pool-${t.id}-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-sm">{d.pool_name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{d.pool_address}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Tank: <span className="mono-num">{d.tank_size}</span> gal</div>
        </div>
        {d.pool_address && (
          <a href={mapsUrl(d.pool_address)} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 px-3 h-9 rounded-sm bg-foreground text-background text-xs font-semibold hover:bg-foreground/90 flex-shrink-0"
             data-testid={`driver-navigate-${t.id}-${idx}`}>
            <Navigation className="w-3.5 h-3.5" /> Drive
          </a>
        )}
      </div>

      {entryEditIdx === idx ? (
        <div className="border border-primary/40 bg-primary/5 rounded-sm p-3 space-y-2" data-testid={`driver-entry-form-${t.id}-${idx}`}>
          <div className="overline text-primary text-[10px]">Edit access info</div>
          <PhotoUploader
            value={entryDraft.entry_photo_data_url}
            onChange={(v) => setEntryDraft({ ...entryDraft, entry_photo_data_url: v })}
            label="Entry / gate photo"
            testId={`driver-entry-photo-upload-${t.id}-${idx}`}
            capture="environment"
            colonyId={t.colony_id}
          />
          <textarea
            className="w-full rounded-sm font-mono text-xs border border-border bg-background p-2 min-h-[80px]"
            placeholder="Gate code 1234#&#10;Lockbox by garage, combo 4-9-1"
            rows={3}
            value={entryDraft.entry_instructions}
            onChange={(e) => setEntryDraft({ ...entryDraft, entry_instructions: e.target.value })}
            data-testid={`driver-entry-text-input-${t.id}-${idx}`}
          />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={cancelEntryEdit} data-testid={`driver-entry-cancel-${t.id}-${idx}`}>Cancel</Button>
            <Button size="sm" className="rounded-sm h-9" onClick={() => saveEntryEdit(idx)} data-testid={`driver-entry-save-${t.id}-${idx}`}>Save</Button>
          </div>
        </div>
      ) : (
        <div className="border border-primary/30 bg-primary/5 rounded-sm p-2" data-testid={`driver-entry-${t.id}-${idx}`}>
          <div className="flex items-start gap-2">
            {d.entry_photo_data_url && (
              <LivePhotoSurface value={d.entry_photo_data_url} alt="entry" className="w-16 h-16 rounded-sm border border-primary/30 flex-shrink-0" testId={`driver-entry-photo-${t.id}-${idx}`} />
            )}
            <div className="text-xs leading-relaxed min-w-0 flex-1">
              <div className="font-semibold text-primary uppercase tracking-wider text-[10px] mb-0.5">Access info</div>
              <div className="font-mono whitespace-pre-wrap break-words" data-testid={`driver-entry-text-${t.id}-${idx}`}>
                {d.entry_instructions || (d.entry_photo_data_url ? "(Photo only)" : "No access info on file.")}
              </div>
            </div>
            <Button size="sm" variant="ghost" className="rounded-sm h-8 text-primary hover:text-primary flex-shrink-0" onClick={() => startEntryEdit(idx, d)} data-testid={`driver-entry-edit-${t.id}-${idx}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex-shrink-0">Gallons</label>
        <Input type="number" inputMode="decimal" step="0.1" min="0"
               className="rounded-sm h-11 text-lg mono-num text-right font-semibold"
               value={d.gallons}
               onChange={(e) => {
                 const v = parseFloat(e.target.value || 0);
                 setPools(pools.map((x, i) => i === idx ? { ...x, gallons: v } : x));
               }}
               data-testid={`driver-pool-gallons-${t.id}-${idx}`} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PhotoUploader
          value={d.photo_before_data_url || ""}
          onChange={(v) => setPools(pools.map((x, i) => i === idx ? { ...x, photo_before_data_url: v } : x))}
          label="Before (filling tank)"
          testId={`driver-pool-photo-before-${t.id}-${idx}`}
          capture="environment"
          validateAsTank={true}
          validateKind="before"
          validateContext={`Pool "${d.pool_name || ""}" — chlorine tank BEFORE refill (filling stage).`}
          colonyId={t.colony_id}
        />
        <PhotoUploader
          value={d.photo_after_data_url || ""}
          onChange={(v) => setPools(pools.map((x, i) => i === idx ? { ...x, photo_after_data_url: v } : x))}
          label="After (finished)"
          testId={`driver-pool-photo-after-${t.id}-${idx}`}
          capture="environment"
          validateAsTank={true}
          validateKind="after"
          validateContext={`Pool "${d.pool_name || ""}" — chlorine tank AFTER refill (finished).`}
          colonyId={t.colony_id}
        />
      </div>
    </div>
  );
}
